import * as THREE from "three";
import { DISTANT_CAYS, HOME_ISLAND, landSdf, type Cay } from "../coast";
import type { ClubMaterials } from "../materials";
import { buildPalm } from "./vegetation";

function hash(x: number, z: number): number {
  const n = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

function islandHeight(x: number, z: number, cay: Cay = HOME_ISLAND): number {
  const d = landSdf(x, z);
  if (d > 0) return 0;
  const n = (hash(x * 0.07, z * 0.07) - 0.5) * 1.8 + (hash(x * 0.19, z * 0.16) - 0.5) * 0.7;
  const inland = Math.min(-d, 70) / 70;
  const towardCore = Math.max(0, -(z - (cay.cz + cay.rz * 0.15)) / (cay.rz * 1.1));
  const peak = cay.peak * inland * inland * towardCore;
  const beach = THREE.MathUtils.smoothstep(-d, 0, 7);
  let h = 0.2 + beach * 1.55 + peak + n * inland;
  if (cay === HOME_ISLAND && z < -68 && z > -220 && Math.abs(x) < 78 && d < -10) {
    h = 1.86 + n * 0.1;
  }
  return h;
}

function buildTerrain(cay: Cay, segsX: number, segsZ: number, mats: ClubMaterials): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(cay.rx * 2.15, cay.rz * 2.15, segsX, segsZ);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const sand = new THREE.Color(0xc4b18a);
  const grass = new THREE.Color(0x3d7a52);
  const rock = new THREE.Color(0x6a6560);
  const tmp = new THREE.Color();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i) + cay.cx;
    const z = pos.getZ(i) + cay.cz;
    const h = islandHeight(x, z, cay);
    const d = landSdf(x, z);
    if (d > 1.5) {
      pos.setY(i, -5);
      tmp.set(0x9a8866);
    } else {
      pos.setY(i, h);
      if (h < 1.35) tmp.copy(sand);
      else if (h > cay.peak * 0.55 + 4) tmp.copy(rock).lerp(grass, 0.35);
      else tmp.copy(grass).lerp(sand, THREE.MathUtils.clamp((1.8 - h) * 0.4, 0, 0.45));
    }
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
    pos.setX(i, x);
    pos.setZ(i, z);
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  const mat = mats.grass.clone();
  mat.vertexColors = true;
  mat.color.set(0xffffff);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.castShadow = true;
  return mesh;
}

function makeHouse(
  mats: ClubMaterials,
  w: number,
  d: number,
  h: number,
  roof: THREE.Material,
): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mats.stucco);
  body.position.y = h * 0.5;
  body.castShadow = true;
  body.receiveShadow = true;
  g.add(body);
  const roofMesh = new THREE.Mesh(
    new THREE.ConeGeometry(Math.max(w, d) * 0.74, h * 0.48, 4),
    roof,
  );
  roofMesh.rotation.y = Math.PI / 4;
  roofMesh.position.y = h + h * 0.2;
  roofMesh.castShadow = true;
  g.add(roofMesh);
  const win = new THREE.Mesh(new THREE.BoxGeometry(w * 0.18, h * 0.28, 0.08), mats.darkGlass);
  win.position.set(0, h * 0.55, d * 0.5 + 0.02);
  g.add(win);
  const win2 = win.clone();
  win2.position.x = w * 0.28;
  g.add(win2);
  const win3 = win.clone();
  win3.position.x = -w * 0.28;
  g.add(win3);
  return g;
}

function makeCanopyTree(mats: ClubMaterials, scale = 1): THREE.Group {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.28, 3.2, 6), mats.palmTrunk);
  trunk.position.y = 1.6;
  trunk.castShadow = true;
  g.add(trunk);
  for (const [x, y, z, s] of [
    [0, 3.4, 0, 1.6],
    [0.7, 3.1, 0.2, 1.15],
    [-0.55, 3.2, -0.3, 1.2],
  ] as const) {
    const leaf = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 0), mats.canopy);
    leaf.position.set(x, y, z);
    leaf.castShadow = true;
    g.add(leaf);
  }
  g.scale.setScalar(scale);
  return g;
}

function placeRoads(parent: THREE.Group, mats: ClubMaterials): void {
  const y = 1.9;
  const main = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.08, 160), mats.asphalt);
  main.position.set(0, y, -138);
  main.receiveShadow = true;
  parent.add(main);

  for (const z of [-88, -128, -168, -205]) {
    const cross = new THREE.Mesh(new THREE.BoxGeometry(128, 0.08, 5.6), mats.asphalt);
    cross.position.set(0, y, z);
    cross.receiveShadow = true;
    parent.add(cross);
  }

  const plaza = new THREE.Mesh(new THREE.BoxGeometry(32, 0.09, 28), mats.asphalt);
  plaza.position.set(0, y, -108);
  plaza.receiveShadow = true;
  parent.add(plaza);

  const fountain = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.6, 0.55, 14), mats.whiteConcrete);
  fountain.position.set(0, 2.2, -108);
  fountain.castShadow = true;
  parent.add(fountain);
  const water = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 1.7, 0.12, 14), mats.pool);
  water.position.set(0, 2.48, -108);
  parent.add(water);
}

function placeHouses(parent: THREE.Group, mats: ClubMaterials): void {
  const roofs = [mats.roofTerra, mats.roofNavy, mats.coral];
  const rows: Array<{ z: number; xs: number[]; facing: number }> = [
    { z: -80, xs: [-52, -38, -24, 24, 38, 54], facing: 0 },
    { z: -96, xs: [-58, -44, -30, 30, 44, 58], facing: Math.PI },
    { z: -120, xs: [-62, -46, -32, -18, 18, 32, 46, 62], facing: 0 },
    { z: -136, xs: [-58, -42, -28, 28, 42, 58], facing: Math.PI },
    { z: -160, xs: [-64, -48, -34, -20, 20, 34, 48, 64], facing: 0 },
    { z: -176, xs: [-52, -36, 36, 52], facing: Math.PI },
    { z: -198, xs: [-40, -22, 22, 40], facing: 0 },
  ];

  let n = 0;
  for (const row of rows) {
    for (const x of row.xs) {
      if (islandHeight(x, row.z) < 1.4) continue;
      const w = 8 + hash(x, row.z) * 4;
      const d = 7 + hash(x + 3, row.z) * 3;
      const h = 3.1 + hash(x, row.z + 9) * 1.8;
      const house = makeHouse(mats, w, d, h, roofs[n % roofs.length]);
      const ground = islandHeight(x, row.z);
      house.position.set(x, ground, row.z);
      house.rotation.y = row.facing + (hash(x * 2, row.z) - 0.5) * 0.08;
      parent.add(house);
      n += 1;
    }
  }

  const steepleBase = makeHouse(mats, 10, 12, 6.2, mats.roofNavy);
  steepleBase.position.set(18, islandHeight(18, -108), -108);
  parent.add(steepleBase);
  const spire = new THREE.Mesh(new THREE.ConeGeometry(1.1, 5.5, 8), mats.roofNavy);
  spire.position.set(18, islandHeight(18, -108) + 11.2, -108);
  spire.castShadow = true;
  parent.add(spire);
}

function scatterTrees(parent: THREE.Group, mats: ClubMaterials): void {
  let k = 0;
  for (let i = 0; i < 90; i++) {
    const x = (hash(i, 4.2) - 0.5) * 280;
    const z = -55 - hash(i, 9.1) * 210;
    if (Math.abs(x) < 12 && z > -220 && z < -70) continue;
    const h = islandHeight(x, z);
    if (h < 1.5 || h > 16) continue;
    if (landSdf(x, z) > -8) continue;
    if (hash(i, 1.7) > 0.55) {
      const palm = buildPalm(mats, 0.06 + (i % 4) * 0.03);
      palm.position.set(x, h, z);
      palm.rotation.y = i * 0.6;
      palm.scale.setScalar(0.8 + hash(i, 2) * 0.45);
      parent.add(palm);
    } else {
      const tree = makeCanopyTree(mats, 0.85 + hash(i, 3) * 0.5);
      tree.position.set(x, h, z);
      tree.rotation.y = i;
      parent.add(tree);
    }
    k += 1;
    if (k > 70) break;
  }
}

function decorateCay(parent: THREE.Group, cay: Cay, mats: ClubMaterials): void {
  const roofs = [mats.roofTerra, mats.roofNavy, mats.stucco];
  const count = 4 + Math.floor(cay.rx / 18);
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + 0.4;
    const r = cay.rx * 0.28;
    const x = cay.cx + Math.cos(a) * r;
    const z = cay.cz + Math.sin(a) * r * 0.7;
    const h = islandHeight(x, z, cay);
    if (h < 1.2) continue;
    const house = makeHouse(mats, 7 + (i % 3), 6, 3.2 + (i % 2), roofs[i % roofs.length]);
    house.position.set(x, h, z);
    house.rotation.y = a + Math.PI;
    house.scale.setScalar(0.85);
    parent.add(house);
  }
  for (let i = 0; i < 14; i++) {
    const a = hash(cay.cx, i) * Math.PI * 2;
    const r = 8 + hash(i, cay.cz) * (cay.rx * 0.45);
    const x = cay.cx + Math.cos(a) * r;
    const z = cay.cz + Math.sin(a) * r * (cay.rz / cay.rx);
    const h = islandHeight(x, z, cay);
    if (h < 0.8) continue;
    const palm = buildPalm(mats, 0.1);
    palm.position.set(x, h, z);
    palm.scale.setScalar(0.7 + hash(i, 8) * 0.4);
    parent.add(palm);
  }
}

export function buildScenery(mats: ClubMaterials): THREE.Group {
  const g = new THREE.Group();
  g.name = "scenery";
  g.add(buildTerrain(HOME_ISLAND, 72, 68, mats));
  placeRoads(g, mats);
  placeHouses(g, mats);
  scatterTrees(g, mats);

  for (const cay of DISTANT_CAYS) {
    g.add(buildTerrain(cay, 28, 22, mats));
    decorateCay(g, cay, mats);
  }
  return g;
}
